import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Space, Modal, Form, Input, InputNumber, 
  message, Popconfirm, Tag, Card, Typography 
} from 'antd';
import { 
  PlusOutlined, EditOutlined, DeleteOutlined, 
  DollarOutlined, GlobalOutlined 
} from '@ant-design/icons';
import { exchangeRateAPI } from '../api';

const { Title } = Typography;

const ExchangeRateManagement = () => {
  const [exchangeRates, setExchangeRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchExchangeRates();
  }, []);

  const fetchExchangeRates = async () => {
    try {
      setLoading(true);
      const response = await exchangeRateAPI.getAll();
      setExchangeRates(response.data);
    } catch (error) {
      message.error('获取汇率列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingRate(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRate(record);
    form.setFieldsValue({
      currency_code: record.currency_code,
      rate_to_usd: record.rate_to_usd,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await exchangeRateAPI.delete(id);
      message.success('删除汇率成功');
      fetchExchangeRates();
    } catch (error) {
      message.error('删除汇率失败');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      // 将货币代码转换为大写
      values.currency_code = values.currency_code.toUpperCase();
      
      if (editingRate) {
        await exchangeRateAPI.update(editingRate.id, values);
        message.success('更新汇率成功');
      } else {
        await exchangeRateAPI.create(values);
        message.success('创建汇率成功');
      }
      
      setModalVisible(false);
      fetchExchangeRates();
    } catch (error) {
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error(editingRate ? '更新汇率失败' : '创建汇率失败');
      }
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingRate(null);
    form.resetFields();
  };

  const columns = [
    {
      title: '货币代码',
      dataIndex: 'currency_code',
      key: 'currency_code',
      width: '15%',
      render: (text) => (
        <Tag color="blue" icon={<GlobalOutlined />}>
          {text}
        </Tag>
      ),
    },
    {
      title: '汇率（1 USD = ? )',
      dataIndex: 'rate_to_usd',
      key: 'rate_to_usd',
      width: '25%',
      render: (rate, record) => (
        <Space>
          <DollarOutlined style={{ color: '#52c41a' }} />
          <span>1 USD = {rate} {record.currency_code}</span>
        </Space>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '20%',
      render: (time) => new Date(time).toLocaleString(),
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: '20%',
      render: (time) => new Date(time).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      width: '20%',
      render: (_, record) => (
        <Space wrap>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个汇率吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];


  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={3} style={{ margin: 0 }}>
            <GlobalOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            汇率管理
          </Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            添加汇率
          </Button>
        </div>


        <Table
          dataSource={exchangeRates}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          size="middle"
        />
      </Card>

      <Modal
        title={editingRate ? '编辑汇率' : '添加汇率'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={500}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            rate_to_usd: 1.0,
          }}
        >
          <Form.Item
            name="currency_code"
            label="货币代码"
            rules={[
              { required: true, message: '请输入货币代码' },
              { pattern: /^[A-Z]{3}$/, message: '货币代码必须为3位大写字母' },
            ]}
          >
            <Input
              placeholder="例如：CNY, EUR, JPY"
              maxLength={3}
              style={{ textTransform: 'uppercase' }}
            />
          </Form.Item>


          <Form.Item
            name="rate_to_usd"
            label="汇率（1 USD = ? )"
            rules={[
              { required: true, message: '请输入汇率' },
              { type: 'number', min: 0.0001, message: '汇率必须大于0' },
            ]}
          >
            <InputNumber
              placeholder="例如：6.7 (表示 1 USD = 6.7 CNY)"
              min={0.0001}
              step={0.0001}
              precision={4}
              style={{ width: '100%' }}
            />
          </Form.Item>

        </Form>

        <div style={{ 
          marginTop: 16, 
          padding: 12, 
          backgroundColor: '#f0f2f5', 
          borderRadius: 6,
          fontSize: '12px',
          color: '#666'
        }}>
          <div><strong>说明：</strong></div>
          <div>• 汇率表示 1 美元能兑换多少目标货币</div>
          <div>• 例如：1 USD = 6.7 CNY，则汇率填写 6.7</div>
          <div>• 货币代码必须符合 ISO 4217 标准（3位大写字母）</div>
        </div>
      </Modal>
    </div>
  );
};

export default ExchangeRateManagement;
